import Clear from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeSnackbar as closeSnackbarAction, enqueueSnackbar as enqueueSnackbarAction, removeSnackbar } from '../_flux/Actions';
import store, { type FluxStore } from '../_flux/Store';
import { getCallNumber, getConf, getIdTel } from './Session';
import { checkUndefinedNullOrEmpty, checkUndefinedOrNull } from './Tools';

const notifySound = '/sounds/notify.mp3';

const loadingTimeout: any = {};

let displayed = [];

const Toast = () => {
    const dispatch = useDispatch();
    const notifications = useSelector((str: FluxStore) => str.app.notifications || []);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const storeDisplayed = (id) => {
        displayed = [...displayed, id];
    };

    const removeDisplayed = (id) => {
        displayed = [...displayed.filter((key) => id !== key)];
    };

    useEffect(
        function () {
            notifications.forEach(({ key, message, options = {}, dismissed = false }: { key: string; message: string; options: any; dismissed: boolean }) => {
                if (dismissed) {
                    // dismiss snackbar
                    closeSnackbar(key);
                    return;
                }

                if (displayed.includes(key)) return;

                // display snackbar
                enqueueSnackbar(message, {
                    key,
                    ...options,
                    onClose: (event, reason, myKey) => {
                        if (options.onClose) options.onClose(event, reason, myKey);
                    },
                    onExited: (event, myKey) => {
                        // remove this snackbar from redux store
                        dispatch(removeSnackbar(myKey));
                        removeDisplayed(myKey);
                    },
                });

                // keep track of snackbars that we've displayed
                storeDisplayed(key);
            });
        },
        [notifications, closeSnackbar, enqueueSnackbar, dispatch]
    );

    return undefined;
};

export default Toast;

// Fehler im Toaster anzeigen
Toast.addError = (e: { key: string; primary: string; secondary?: string; tertiary?: string; sound?: boolean }) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    // let key;
    // if (!checkUndefinedNullOrEmpty(e?.key)) key = e.key;

    let messagePrimary;
    if (!checkUndefinedNullOrEmpty(e?.primary)) messagePrimary = e.primary;
    else messagePrimary = undefined;

    let messageSecondary;
    if (!checkUndefinedNullOrEmpty(e?.secondary)) messageSecondary = e.secondary;
    else messageSecondary = undefined;

    let messageTertiary;
    if (!checkUndefinedNullOrEmpty(e?.tertiary)) messageTertiary = e.tertiary;
    else messageTertiary = undefined;

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));
    const message = [];
    if (messagePrimary)
        message.push(
            <p key="messagePrimary" style={{ fontWeight: 700, marginBottom: 0, whiteSpace: 'nowrap' }}>
                {messagePrimary}
            </p>
        );
    if (messageSecondary)
        message.push(
            <p key="messageSecondary" style={{ marginBottom: 0 }}>
                {messageSecondary}
            </p>
        );
    if (messageTertiary)
        message.push(
            <p key="messageTertiary" style={{ marginBottom: 0 }}>
                {messageTertiary}
            </p>
        );

    if (soundSettings && e.sound) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: <div>{message}</div>,
        options: {
            key: /* key || */ new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: 'error',
            autoHideDuration: 10000,
            persist: false,
            action: (key) => (
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>
            ),
        },
    });
};

// Warning im Toaster anzeigen
Toast.addWarning = (e: { key: string; primary: string; secondary?: string; tertiary?: string; sound?: boolean }) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    // let key;
    // if (!checkUndefinedNullOrEmpty(e?.key)) key = e.key;

    let messagePrimary;
    if (!checkUndefinedNullOrEmpty(e?.primary)) messagePrimary = e.primary;
    else messagePrimary = undefined;

    let messageSecondary;
    if (!checkUndefinedNullOrEmpty(e?.secondary)) messageSecondary = e.secondary;
    else messageSecondary = undefined;

    let messageTertiary;
    if (!checkUndefinedNullOrEmpty(e?.tertiary)) messageTertiary = e.tertiary;
    else messageTertiary = undefined;

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));
    const message = [];
    if (messagePrimary)
        message.push(
            <p key="messagePrimary" style={{ fontWeight: 700, marginBottom: 0, whiteSpace: 'nowrap' }}>
                {messagePrimary}
            </p>
        );
    if (messageSecondary)
        message.push(
            <p key="messageSecondary" style={{ marginBottom: 0 }}>
                {messageSecondary}
            </p>
        );
    if (messageTertiary)
        message.push(
            <p key="messageTertiary" style={{ marginBottom: 0 }}>
                {messageTertiary}
            </p>
        );

    if (soundSettings && e.sound) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: <div>{message}</div>,
        options: {
            key: /* key || */ new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: 'warning',
            autoHideDuration: 6000,
            persist: false,
            action: (key) => (
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>
            ),
        },
    });
};

// Success im Toaster anzeigen
Toast.addSuccess = (e: { key: string; primary: string; secondary?: string; tertiary?: string; sound?: boolean }) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    // let key;
    // if (!checkUndefinedNullOrEmpty(e?.key)) key = e.key;

    let messagePrimary;
    if (!checkUndefinedNullOrEmpty(e?.primary)) messagePrimary = e.primary;
    else messagePrimary = undefined;

    let messageSecondary;
    if (!checkUndefinedNullOrEmpty(e?.secondary)) messageSecondary = e.secondary;
    else messageSecondary = undefined;

    let messageTertiary;
    if (!checkUndefinedNullOrEmpty(e?.tertiary)) messageTertiary = e.tertiary;
    else messageTertiary = undefined;

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));
    const message = [];
    if (messagePrimary)
        message.push(
            <p key="messagePrimary" style={{ fontWeight: 700, marginBottom: 0, whiteSpace: 'nowrap' }}>
                {messagePrimary}
            </p>
        );
    if (messageSecondary)
        message.push(
            <p key="messageSecondary" style={{ marginBottom: 0 }}>
                {messageSecondary}
            </p>
        );
    if (messageTertiary)
        message.push(
            <p key="messageTertiary" style={{ marginBottom: 0 }}>
                {messageTertiary}
            </p>
        );

    if (soundSettings && e.sound) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: <div>{message}</div>,
        options: {
            key: /* key || */ new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: 'success',
            autoHideDuration: message.join('').length > 50 ? 6000 : 3000,
            persist: false,
            action: (key) => (
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>
            ),
        },
    });
};

// Info im Toaster anzeigen
Toast.addInfo = (e: { key: string; primary: string; secondary?: string; tertiary?: string; sound?: boolean }) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    // let key;
    // if (!checkUndefinedNullOrEmpty(e?.key)) key = e.key;

    let messagePrimary;
    if (!checkUndefinedNullOrEmpty(e?.primary)) messagePrimary = e.primary;
    else messagePrimary = undefined;

    let messageSecondary;
    if (!checkUndefinedNullOrEmpty(e?.secondary)) messageSecondary = e.secondary;
    else messageSecondary = undefined;

    let messageTertiary;
    if (!checkUndefinedNullOrEmpty(e?.tertiary)) messageTertiary = e.tertiary;
    else messageTertiary = undefined;

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));
    const message = [];
    if (messagePrimary)
        message.push(
            <p key="messagePrimary" style={{ fontWeight: 700, marginBottom: 0, whiteSpace: 'nowrap' }}>
                {messagePrimary}
            </p>
        );
    if (messageSecondary)
        message.push(
            <p key="messageSecondary" style={{ marginBottom: 0 }}>
                {messageSecondary}
            </p>
        );
    if (messageTertiary)
        message.push(
            <p key="messageTertiary" style={{ marginBottom: 0 }}>
                {messageTertiary}
            </p>
        );

    if (soundSettings && e.sound) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: <div>{message}</div>,
        options: {
            key: /* key || */ new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: 'info',
            autoHideDuration: message.join('').length > 50 ? 6000 : 3000,
            persist: false,
            action: (key) => (
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>
            ),
        },
    });
};

// Loading im Toaster anzeigen
Toast.addLoading = (e: { key: string; primary: string; secondary?: string; tertiary?: string; timeout?: number }) => {
    let key;
    if (!checkUndefinedNullOrEmpty(e?.key)) key = e.key;
    else {
        console.error('Toast-Fehler!\nFür ein Loading-Toast muss zwingend eine ID vergeben werden!');
        return;
    }

    loadingTimeout[key] = setTimeout(() => {
        let messagePrimary;
        if (!checkUndefinedNullOrEmpty(e?.primary)) messagePrimary = e.primary;
        else messagePrimary = undefined;

        let messageSecondary;
        if (!checkUndefinedNullOrEmpty(e?.secondary)) messageSecondary = e.secondary;
        else messageSecondary = undefined;

        let messageTertiary;
        if (!checkUndefinedNullOrEmpty(e?.tertiary)) messageTertiary = e.tertiary;
        else messageTertiary = undefined;

        // @ts-expect-error
        const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
        const message = [];
        if (messagePrimary)
            message.push(
                <p key="messagePrimary" style={{ fontWeight: 700, marginBottom: 0, whiteSpace: 'nowrap' }}>
                    {messagePrimary}
                </p>
            );
        if (messageSecondary)
            message.push(
                <p key="messageSecondary" style={{ marginBottom: 0 }}>
                    {messageSecondary}
                </p>
            );
        if (messageTertiary)
            message.push(
                <p key="messageTertiary" style={{ marginBottom: 0 }}>
                    {messageTertiary}
                </p>
            );

        enqueueSnackbar({
            message: <div>{message}</div>,
            options: {
                key: key,
                variant: 'default',
                persist: true,
            },
        });
    }, e.timeout || 0);
};

// Loading im Toaster entfernen
Toast.removeLoading = (key) => {
    if (checkUndefinedNullOrEmpty(key)) {
        console.error('Toast-Fehler!\nFür ein Loading-Toast muss zwingend eine ID vergeben werden!');
        return;
    }
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));
    closeSnackbar(key);
    clearTimeout(loadingTimeout[key]);
};

Toast.addMsgWS = (title, messagePrimary, level) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));

    if (soundSettings) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: [title, messagePrimary].join('\n'),
        options: {
            key: new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: level,
            persist: true,
            action: (key) => (
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>
            ),
        },
    });
};

Toast.addSysMsgWS = (title, messagePrimary, level) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    const url = '/leads/offen';
    const Link1 = (props) => <Link {...props} />;

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));

    if (soundSettings) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: [title, messagePrimary].join('\n'),
        options: {
            key: new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: level,
            persist: true,
            action: (key) => [
                <Button key="btn1" className="noHoverColor" sx={{ color: 'common.white' }} component={Link1} to={url} onClick={() => closeSnackbar(key)}>
                    Anzeigen
                </Button>,
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn2">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>,
            ],
        },
    });
};

Toast.addMsgWSAntwortKunde = (title, messagePrimary, level, id) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    const url = '/leads/lead/' + id;
    const Link1 = (props) => <Link {...props} />;

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));

    if (soundSettings) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: [title, messagePrimary].join('\n'),
        options: {
            key: new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: level,
            persist: true,
            action: (key) => [
                <Button key="btn1" className="noHoverColor" sx={{ color: 'common.white' }} component={Link1} to={url} onClick={() => closeSnackbar(key)}>
                    Anzeigen
                </Button>,
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn2">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>,
            ],
        },
    });
};

Toast.addMsgWSFehlerSenden = (title, messagePrimary, level, id) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    const url = '/leads/lead/' + id;
    const Link1 = (props) => <Link {...props} />;

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));

    if (soundSettings) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: [title, messagePrimary].join('\n'),
        options: {
            key: new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: level,
            persist: true,
            action: (key) => [
                <Button key="btn1" className="noHoverColor" sx={{ color: 'common.white' }} component={Link1} to={url} onClick={() => closeSnackbar(key)}>
                    Anzeigen
                </Button>,
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn2">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>,
            ],
        },
    });
};

Toast.addCallerMsgWS = (title, messagePrimary, level, id) => {
    const sessionSettings = getConf();
    const soundSettings = !checkUndefinedOrNull(sessionSettings?.main) && sessionSettings?.main.sounds;

    const url = { pathname: '/clients/' + id };
    const Link1 = (props) => <Link {...props} />;
    let strRecall = undefined;
    if (0 === id && !checkUndefinedOrNull(getIdTel())) {
        strRecall = 'http://91.137.78.38:8080/telefon/call/' + getIdTel() + '/' + getCallNumber(messagePrimary) + '/0';
    }

    // @ts-expect-error
    const enqueueSnackbar = (...args) => store.dispatch(enqueueSnackbarAction(...args));
    // @ts-expect-error
    const closeSnackbar = (...args) => store.dispatch(closeSnackbarAction(...args));

    if (soundSettings) {
        const audio = new Audio(notifySound);
        audio.play();
    }

    enqueueSnackbar({
        message: [title, messagePrimary].join('\n'),
        options: {
            key: new Date().getTime() + Math.floor(Math.random() * Math.floor(999999)),
            variant: level,
            persist: true,
            action: (key) => [
                <Button key="btn1" className="noHoverColor" sx={{ color: 'common.white' }} component={Link1} to={strRecall || url} onClick={() => closeSnackbar(key)}>
                    {strRecall ? messagePrimary : 'Zur Firma'}
                </Button>,
                <Tooltip aria-label="closeNotify" title="Schließen" key="btn2">
                    <IconButton sx={{ color: 'common.white' }} onClick={() => closeSnackbar(key)} size="large">
                        <Clear />
                    </IconButton>
                </Tooltip>,
            ],
        },
    });
};

Toast.allowDesktopNotifications = () => {
    if (!('Notification' in window)) return;
    else if (Notification.permission !== 'granted' && Notification.permission !== 'denied') Notification.requestPermission();
};

Toast.showNotification = (title, bodyText, imageURL?, actionName?, dataObject?) => {
    if (!document.hasFocus()) {
        let notification = undefined;

        const options: NotificationOptions = {
            tag: actionName,
            body: bodyText,
            // image: imageURL,
            icon: '/favicon/web-app-manifest-512x512.png',
            // badge: <URL String>,
            dir: 'ltr',
            data: dataObject,
            requireInteraction: true,
        };
        // Let's check if the browser supports notifications
        if (!('Notification' in window)) return;
        // Let's check whether notification permissions have already been granted
        else if (Notification.permission === 'granted') {
            notification = new Notification(title, options); // If it's okay let's create a notification
            notification.onclick = function (event) {
                Toast.notificationClick(event);
            };
        } else if (Notification.permission !== 'denied') {
            // Otherwise, we need to ask the user for permission
            Notification.requestPermission().then(function (permission) {
                // If the user accepts, let's create a notification
                if (permission === 'granted') {
                    notification = new Notification(title, options);
                    notification.onclick = function (event) {
                        Toast.notificationClick(event);
                    };
                }
            });
        }

        // At last, if the user has denied notifications, and you
        // want to be respectful there is no need to bother them any more.
        return notification;
    }
};

Toast.notificationClick = (e: any) => {
    switch (e.target.tag) {
        case 'sysMsgWs':
            window.location.href = '/leads/offen';
            // @ts-expect-error
            this.close();
            break;
        case 'msgWsAntwortKunde':
        case 'msgWsFehlerSenden':
            window.location.href = '/leads/lead/' + e.target.data.id;
            // @ts-expect-error
            this.close();
            break;
        case 'callerMsgWs':
            if (0 === e.target.data.id && !checkUndefinedOrNull(getIdTel())) window.open('http://91.137.78.38:8080/telefon/call/' + getIdTel() + '/' + getCallNumber(e.target.body) + '/0');
            else window.location.href = '/clients/' + e.target.data.id;
            // @ts-expect-error
            this.close();
            break;
        case 'globalNotifyClick':
            window.location = e.target.data.url;
            // @ts-expect-error
            this.close();
            break;
        default:
            // @ts-expect-error
            this.close();
            break;
    }
};
